import { Box, Grid, Typography, Theme } from '@mui/material';
import { SubHeading } from '../investors/components';
import React from 'react';
import { Item, ItemDetails, Gridstyles } from './index';
import { ApplicationProps } from '../../redux-store/types/api-types';
import { formatShortDate } from '../../utils/date';
import { SxProps } from '@mui/system';
import {
  applicantStatusMasters,
  BooleanYesNoMaster,
  IdentityProofsMasters,
  maritalStatusMasters,
  YesNoMaster,
} from '../../utils/constant';
import {
  checkForMaritalStatusOthers,
  getAge,
  getOccupationType,
  isApplicantNRI,
  isCustodianAxis,
  isCustodianICICIOrHDFC,
  isCustodianKotak,
  isSameNameAsOvdFalse,
} from '../../utils/utilityFunctions';
import { useCustodianData } from '../../utils/useCustodianData';

export const KeyValue = ({
  title,
  description,
  amount,
  sx = {},
  descriptionSx = {},
}: {
  title: string | undefined;
  description?: string | number | boolean | null;
  amount?: number | null | undefined | string;
  sx?: SxProps<Theme>;
  descriptionSx?: SxProps<Theme>;
}): JSX.Element => {
  return (
    <Grid item xs={12} sm={6}>
      <Item sx={sx}>{title}</Item>
      <ItemDetails sx={descriptionSx}> {description ? description : '-'}</ItemDetails>
      {!!amount && amount !== 0 && <Item sx={{ pt: 0 }}> {amount}</Item>}
    </Grid>
  );
};

export default function ContributorDetails({
  application,
  modeOfHolding,
}: {
  application: ApplicationProps;
  modeOfHolding: string;
}): JSX.Element {
  const [custodianData] = useCustodianData();
  const { applicants = [] } = application || {};
  const selectedCustodian = application?.custodian || '';
  return (
    <>
      <Box
        sx={{
          bgcolor: 'white',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
          borderRadius: '10px',
        }}
        mt={5}>
        <Box sx={{ p: 2 }}>
          <Grid container sx={{ mt: 1, pl: { xs: 2, sm: 10 }, pr: { xs: 2, sm: 10 } }}>
            <Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
              <Typography
                component="h2"
                sx={{ fontSize: 18, fontWeight: 600, color: 'primary.main' }}>
                Applicant(s) Details
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: 'text.secondary',
                }}>
                Mode of Holding:{' '}
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 500,
                    fontSize: '16px',
                    color: 'rgba(0, 0, 0, 0.6)',
                    letterSpacing: '0.4px',
                    ml: 0.5,
                    textTransform: 'capitalize',
                  }}>
                  {modeOfHolding === 'either or survivor' ? 'Joint' : modeOfHolding || 'Single'}
                </Typography>
              </Typography>
            </Grid>
            {applicants.map((applicant, index: number) => {
              const title = [
                'First Applicant Personal Details',
                'Second Applicant Personal Details',
                'Third Applicant Personal Details',
              ][index];
              return (
                <React.Fragment key={applicant.id}>
                  <SubHeading
                    sx={{
                      color: 'common.black',
                      letterSpacing: 0.5 + 'px',
                      padding: { xs: '10px 10px', sm: '10px 30px' },
                    }}>
                    {title}
                  </SubHeading>
                  <Gridstyles>
                    <Grid
                      item
                      container
                      pb={5}
                      rowSpacing={6}
                      columnSpacing={13}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start">
                      <KeyValue title={'Full Name'} description={applicant.name} />
                      <KeyValue title={'First Name'} description={applicant.firstName} />
                      <KeyValue title={'Last Name'} description={applicant.lastName} />
                      <KeyValue title={'Gender'} description={applicant.gender} />
                      <KeyValue title={'Mother Name'} description={applicant.motherName} />
                      <KeyValue title={'Father/Spouse'} description={applicant.fatherOrSpouse} />
                      <KeyValue
                        title={'Father/Spouse Name'}
                        description={applicant.fatherOrSpouseName}
                      />
                      <KeyValue
                        title={'Date Of Birth'}
                        description={formatShortDate(applicant.dateOfBirth)}
                      />
                      <KeyValue
                        title="Age"
                        description={Math.floor(getAge(applicant.dateOfBirth) || 0)}
                      />
                      {/* <KeyValue
                        title={'Marital Status'}
                        description={
                          applicant?.maritalStatus &&
                          applicant.maritalStatusOthers &&
                          checkForMaritalStatusOthers(selectedCustodian, applicant?.maritalStatus)
                            ? applicant.maritalStatusOthers
                            : maritalStatusMasters[applicant.maritalStatus || '']
                        }
                      /> */}
                      {applicant.kraMaritalStatus && (
                        <KeyValue
                          title={'KRA Marital Status'}
                          description={applicant.kraMaritalStatus}
                        />
                      )}
                      <KeyValue
                        title={'Occupation Type'}
                        description={applicant.occupationDetails}
                      />
                      {isCustodianAxis(selectedCustodian) &&
                        !getOccupationType(applicant.occupationDetails) && (
                          <KeyValue
                            title={'Brief Description'}
                            description={applicant.occupationDescription}
                          />
                        )}
                      <KeyValue title={'Provide Aadhaar'} description={applicant.aadhaarCheck} />
                      {applicant.aadhaarCheck === 'Yes' && (
                        <KeyValue
                          title={'Aadhar Last 4 Digit'}
                          description={applicant.aadhaarNumber}
                        />
                      )}
                      <KeyValue
                        title={'Status'}
                        description={
                          applicant.status
                            ? custodianData && isCustodianICICIOrHDFC(selectedCustodian)
                              ? custodianData[applicant.status]?.displayText
                              : applicantStatusMasters[applicant.status]
                            : applicant.status
                        }
                      />
                      {custodianData &&
                        applicant.status &&
                        isCustodianICICIOrHDFC(selectedCustodian) && (
                          <KeyValue
                            title={'Investor SubType'}
                            description={
                              custodianData[applicant.status]?.subType[
                                applicant.investorSubType || ''
                              ]?.displayText
                            }
                          />
                        )}
                      {isApplicantNRI(applicant.status || '') && (
                        <KeyValue
                          title={'Date of becoming NRI'}
                          description={formatShortDate(applicant.dateOfBecomeNRI)}
                        />
                      )}
                      <KeyValue
                        title={'Nationality of Applicant'}
                        description={applicant.nationality}
                      />
                      <KeyValue title={'PAN'} description={applicant.panNo} />
                      <KeyValue
                        title={'AML Verified'}
                        description={applicant.amlCheck ? YesNoMaster.yes : YesNoMaster.no}
                      />
                      <KeyValue
                        title={'CVL KRA Verified'}
                        description={applicant.kraVerified}
                        descriptionSx={{
                          textTransform: 'capitalize',
                        }}
                      />{' '}
                      {isCustodianKotak(selectedCustodian) && (
                        <>
                          <KeyValue
                            title={'Same name as per OVD documents'}
                            description={
                              BooleanYesNoMaster.find(
                                (_yesNoMaster) =>
                                  _yesNoMaster.key === applicant.sameNameAsOvd?.toString()
                              )?.value
                            }
                          />
                          {!applicant.sameNameAsOvd && (
                            <>
                              <KeyValue
                                title={'Name as per OVD'}
                                description={applicant.nameAsPerOvd || ''}
                              />
                              <KeyValue
                                title={'OVD name'}
                                description={
                                  IdentityProofsMasters[applicant.ovdIdentityProof || '']
                                }
                              />

                              {IdentityProofsMasters[applicant.ovdIdentityProof || ''] ===
                                IdentityProofsMasters.others && (
                                <KeyValue
                                  title={'Other Identity Proof Name'}
                                  description={applicant.otherOvdIdentityProof || ''}
                                />
                              )}
                            </>
                          )}
                        </>
                      )}
                      {isCustodianAxis(selectedCustodian) && (
                        <>
                          <KeyValue
                            title={'Existing Customer'}
                            description={YesNoMaster[applicant.isExisitngCustomer || '']}
                          />
                          {YesNoMaster[applicant.isExisitngCustomer || ''] === YesNoMaster.yes && (
                            <KeyValue title={'Customer Id'} description={applicant.customerId} />
                          )}
                        </>
                      )}
                      {/* <KeyValue
                        title={'Status'}
                        description={
                          applicant.status
                            ? applicantStatusMasters[applicant.status]
                            : applicant.status
                        }
                      /> */}
                      {index !== 0 && (
                        <KeyValue
                          title={'RelationShip With First Applicant'}
                          description={applicant.relationShipWithFirstApplicant}
                        />
                      )}
                    </Grid>
                  </Gridstyles>
                  <SubHeading
                    sx={{
                      color: 'common.black',
                      letterSpacing: 0.5 + 'px',
                      padding: { xs: '10px 10px', sm: '10px 30px' },
                    }}>
                    Applicant occupation and education details
                  </SubHeading>
                  <Box sx={{ width: '100%' }}>
                    <Gridstyles>
                      <Grid
                        item
                        container
                        pb={5}
                        rowSpacing={6}
                        columnSpacing={13}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start">
                        <KeyValue
                          title={'Educational Qualification'}
                          description={applicant.educationalQualification}
                        />
                        <KeyValue title={'Tax Assessee'} description={applicant.taxAssesseeCheck} />
                      </Grid>
                    </Gridstyles>
                  </Box>
                </React.Fragment>
              );
            })}
          </Grid>
        </Box>
      </Box>
    </>
  );
}
